import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { unitsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface UnitReportsProps {
  period: string;
}

interface UnitData {
  id: string;
  name: string;
  floor: string;
  size: number;
  bedrooms: number;
  bathrooms: number;
  shareNumerator: number;
  shareDenominator: number;
  status: "occupied" | "vacant" | "pending";
  residentCount: number;
  currentCharges: number;
}

const UnitReports: React.FC<UnitReportsProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unitData, setUnitData] = useState<UnitData[]>([]);
  const [totalUnits, setTotalUnits] = useState(0);
  const [occupancyRate, setOccupancyRate] = useState(0);
  const [totalResidents, setTotalResidents] = useState(0);
  const [averageUnitSize, setAverageUnitSize] = useState(0);

  useEffect(() => {
    const fetchUnitData = async () => {
      if (!currentBuilding?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const units = await unitsApi.getAll(currentBuilding.id);
        setUnitData(units);
        setTotalUnits(units.length);
        const occupied = units.filter(
          (u: any) => u.status === "occupied",
        ).length;
        setOccupancyRate(
          units.length > 0 ? Math.round((occupied / units.length) * 100) : 0,
        );
        const residents = units.reduce(
          (sum: number, u: any) => sum + (u.residentCount || 0),
          0,
        );
        setTotalResidents(residents);
        const totalSize = units.reduce(
          (sum: number, u: any) => sum + (u.size || 0),
          0,
        );
        setAverageUnitSize(
          units.length > 0 ? Math.round(totalSize / units.length) : 0,
        );
      } catch (err) {
        console.error("Error fetching unit report data:", err);
        setError("Failed to load unit reports. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUnitData();
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading unit reports..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Unit Reports</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Units
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {totalUnits}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Occupancy Rate
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {occupancyRate}%
            </dd>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div
                className={`h-2.5 rounded-full ${
                  occupancyRate >= 90
                    ? "bg-green-600"
                    : occupancyRate >= 75
                    ? "bg-yellow-400"
                    : "bg-red-600"
                }`}
                style={{ width: `${occupancyRate}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Residents
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {totalResidents}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Average Unit Size
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {averageUnitSize} m²
            </dd>
          </div>
        </div>
      </div>

      {/* Units Table */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Unit Details
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Unit
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Floor
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Size (m²)
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Beds/Baths
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Share (%)
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Residents
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Charges
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {unitData.map((unit) => (
                  <tr key={unit.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                      <a href={`/dashboard/units/${unit.id}`}>{unit.name}</a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {unit.floor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {unit.size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {unit.bedrooms}B/{unit.bathrooms}B
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {unit.shareNumerator}/{unit.shareDenominator}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          unit.status === "occupied"
                            ? "bg-green-100 text-green-800"
                            : unit.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {unit.status.charAt(0).toUpperCase() +
                          unit.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {unit.residentCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {fmt(unit.currentCharges)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Unit Status Distribution
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* Simple pie chart visualization */}
            <div className="flex justify-center">
              <div className="w-48 h-48 rounded-full border-8 border-gray-200 relative">
                {/* This would ideally be a real chart with proper calculations */}
                <div
                  className="absolute bg-green-500 rounded-tl-full"
                  style={{ width: "50%", height: "50%", top: 0, left: 0 }}
                ></div>
                <div
                  className="absolute bg-yellow-500 rounded-tr-full"
                  style={{ width: "50%", height: "50%", top: 0, right: 0 }}
                ></div>
                <div
                  className="absolute bg-gray-400 rounded-bl-full"
                  style={{ width: "50%", height: "50%", bottom: 0, left: 0 }}
                ></div>
                <div
                  className="absolute bg-primary-500 rounded-br-full"
                  style={{ width: "50%", height: "50%", bottom: 0, right: 0 }}
                ></div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 mr-2"></div>
                <span className="text-xs text-gray-600">Occupied: 80%</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 mr-2"></div>
                <span className="text-xs text-gray-600">Pending: 10%</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-400 mr-2"></div>
                <span className="text-xs text-gray-600">Vacant: 10%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Unit Size Distribution
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* Simple bar chart visualization */}
            <div className="flex items-end h-48 space-x-2">
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "35%" }}
              ></div>
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "60%" }}
              ></div>
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "80%" }}
              ></div>
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "50%" }}
              ></div>
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "40%" }}
              ></div>
              <div
                className="flex-1 bg-primary-500"
                style={{ height: "30%" }}
              ></div>
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-600">
              <span>50-60m²</span>
              <span>60-70m²</span>
              <span>70-80m²</span>
              <span>80-90m²</span>
              <span>90-100m²</span>
              <span>100+m²</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitReports;
